import React, {ComponentType, useEffect, useState} from "react";
import BookingInfo from "./index";
import {IContainerProps, IHandlers, IProps, IServices, IBookingTimeProps} from './types';
import {first} from 'rxjs/operators';

/**
 * Gets the interface types as array of strings so we don't need hand code them each time (and reduces chance for mistakes).
 */
import {keys} from 'ts-transformer-keys';
import {hot} from "react-hot-loader";
import ReactUtilsService from "app/services/reactUtils.service";
import AppThemeService from 'app/services/theme/appTheme.service';
import {ITagIcon} from "external-components/tag-icon/types";
import {IconTypes} from "app/services/icons/icons.types";
import { BookingType, ICustomer } from "app/app.types";
import {BookingStatusNames} from "app/bookingStatusTypes";
import { CanShowBookingEndTime, DetermineBookingTime, IsBookingEndTimeInvalid } from "./helper";
import TagsService from "external-services/tags-service";
import { CustomerGeneratorService } from "external-services/customer-generator-service";
import CustomerService from "internal-services/customer/customer.service";
import DateUtilsService from 'external-services/date-utils-service';
import CustomerLookupService from 'external-services/customer-lookup-service';

const NS = 'BookingInfoContainer';

const propsKeys: string[] = keys<IProps>();
const servicesKeys: string[] = keys<IServices>();
const containerPropsKeys: string[] = keys<IContainerProps>();

const theme = AppThemeService.getAppTheme();

const ThemeWrap = function(all: IProps & IHandlers & IServices & IContainerProps) {

  /**
   * Sorts 'all' into IServices, IProps and IContainerProps
   */
  const props: IProps = ReactUtilsService.getObjectByKeys(all, propsKeys) as IProps;
  // const {}: IServices = ReactUtilsService.getObjectByKeys(all, servicesKeys) as IServices;
  const {info, fulldate, enableEndBookingTime, defaultDurationInMinutes, isCompact}: IContainerProps = ReactUtilsService.getObjectByKeys(all, containerPropsKeys) as IContainerProps;
  const getCustomerInfo = (_id: string) => CustomerLookupService.getById(_id);
  const handlers: IHandlers = {
    getCustomerInfo
  }
  // This is async (via service), so need useState
  const [tagIcons, setTagIcons] = useState<ITagIcon[]>(null);

  const checkExpired = () => (
    info.status && info.status.name === BookingStatusNames.StandbyPending
      ? DateUtilsService.checkIfStandbyConfirmationExpired(info.standByConfirmationExpiry)
      : false
  );

  // This is async, via useEffect polling, so need useState
  const [isExpired, setIsExpired] = useState<boolean>(checkExpired());

  const expiredPropsWatch = [
    // watches to see if any of these props change before re-running
    info.standByConfirmationExpiry
  ];
  if (info.status) {
    // not sure why info.status was sometimes undefined, but handling it to stop JS error
    expiredPropsWatch.push(info.status.name);
  }

  useEffect(() => {
    // don't poll normal bookings, just standby ones
    if (info.bookingType !== BookingType.StandbyList) {
      setIsExpired(false);
      return () => {};
    }

    setIsExpired(checkExpired());

    const interval = setInterval(() => setIsExpired(checkExpired()), 8000);
    return () => clearInterval(interval); // clears the interval when component exits
  }, expiredPropsWatch);

  props.tagIcons = tagIcons;
  props.bookingRef = info.bookingRef;
  props.isExpired = isExpired;
  props.isFunctionTrackerBooking = info.functionTracker;

  // props.showMinimal = showMinimal;
  props.isCompact = isCompact;

  const bookingTimeProps: IBookingTimeProps = {
    bookingTime: null,
    fulldate: fulldate,
  };

  props.displayStartDate = DetermineBookingTime({...bookingTimeProps, bookingTime: info.time});
  props.displayEndDate = DetermineBookingTime({...bookingTimeProps, bookingTime: info.bookingEnd || DateUtilsService.getBookingEnd({time: info.time, duration: info.duration as number})});

  props.isBookingEndTimeInvalid = IsBookingEndTimeInvalid(info.duration, defaultDurationInMinutes);
  props.canShowBookingEndTime = CanShowBookingEndTime(enableEndBookingTime, info.duration);

  if (!props.displayName) {
    props.displayName = CustomerGeneratorService.getDisplayName(info);
  }

  useEffect(() => {
    const data: any = info;
    const subs = TagsService.getBookingTags$(data, true)
      .pipe(first())
      .subscribe((tagIcons: ITagIcon[]) => {

        // updates the notes title to be more descriptive (not sure this is really needed though - Jim Doyle)
        const notesTag = tagIcons.find(({icon}) => icon === 'notes');
        if (notesTag) {
          notesTag.customerId = info.customerId;
          notesTag.hasCustomerNotes = info.hasCustomerNotes;
          if (info.notes && info.notes.trim().length !== 0) {
            notesTag.title = "Booking Notes:" + "\n" + info.notes;
          } else {
            notesTag.title = "";
          }
        }

        // inverts the walkin icon
        if (info.bookingType === BookingType.WaitList) {
          const walkInTag = tagIcons.find(({icon}) => icon === 'walkin');
          if (walkInTag) {
            walkInTag.colour = theme.palette.primary.main;
            walkInTag.isInverted = true;
          }
        }

        // replaces the walkin icon with a standby list icon
        if (info.bookingType === BookingType.StandbyList) {
          tagIcons.unshift({
            iconType: IconTypes.standbyList,
            name: 'Standby List',
            colour: theme.palette.primary.main,
            isInverted: true
          });
          const walkInTagIndex = tagIcons.findIndex(({icon}) => icon === 'walkin');
          if (walkInTagIndex > -1) {
            tagIcons.splice(walkInTagIndex, 1);
          }
        }
        // props.tagIcons = tagIcons
        setTagIcons(tagIcons); // watches for changes to these objects
      });

    return () => {
      subs.unsubscribe();
    };
  }, [info.locked, info.tags]);

  return ReactUtilsService.containerWrap(
    <BookingInfo {...props}  {...handlers} />
  );
}

/**
 * Returns properties needed for rendering react2angular
 */
export const R2ABookingInfo: [ComponentType<unknown>, never[]?, string[]?] = [
  hot(module)(ThemeWrap) as ComponentType<unknown>,
  [...containerPropsKeys] as never[]
];
